import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { DialogService } from '@ngneat/dialog';
import { CourseEntity } from '@core/entities';
import { ApprovedStatus, CourseInstructorType } from '@core/enums';
import { DefaultImageDirective, FetchProfileImageDirective } from '@shared/directives';
import { GetUserId } from '@core/utils';
import { TippyModule } from '@ngneat/helipopper';
import { BadgeComponent, ButtonComponent, IconComponent } from '@shared/components/ui';
import { CourseDetailsComponent } from '@shared/components';
import { FriendlyDatePipe } from '@shared/pipes';
import { PermissionService } from '@auth';

@Component({
  selector: 'app-course-card',
  imports: [CommonModule, IconComponent, ButtonComponent, RouterLink, TranslateModule, DefaultImageDirective, FetchProfileImageDirective, TippyModule, FriendlyDatePipe, BadgeComponent],
  templateUrl: './course-card.component.html',
  styleUrls: ['./course-card.component.scss'],
})
export class CourseCardComponent {
  @Input() isMobileView = false;
  @Input() course: CourseEntity;
  @Output() deleteCourse = new EventEmitter<CourseEntity>();
  @Output() editCourse = new EventEmitter<CourseEntity>();
  @Output() joinCourse = new EventEmitter<CourseEntity>();
  protected readonly InstructorType = CourseInstructorType;
  protected readonly ApprovedStatus = ApprovedStatus;
  private readonly _router = inject(Router);
  private readonly _dialogService = inject(DialogService);
  private readonly _permissionService = inject(PermissionService);

  get instructorType(): string {
    return this.course.instructorType === CourseInstructorType.Internal ? 'Internal Instructor' : 'External Instructor';
  }

  get availabilityCount(): number {
    // return this.course.courseUsers.filter((value) => value.approvedStatus === ApprovedStatus.Approved).length;
    return this.course.courseUsers.length;
  }

  get isCourseFullyBooked(): boolean {
    return this.availabilityCount >= this.course.allowedPersons;
  }

  get availableToJoin(): boolean {
    // This below code is commented to allow authors to join their own courses
    // if (+this.course?.author === +GetUserId()) return false;
    const userExists = this.course.courseUsers.some((value) => +value.userId === +GetUserId());
    return !userExists;
  }

  get courseUserStatus(): string {
    const courseUser = this.course.courseUsers.find((value) => +value.userId === +GetUserId());
    switch (courseUser?.approvedStatus) {
      case ApprovedStatus.Approved:
        return 'courses.joined';
      case ApprovedStatus.Pending:
        return 'Pending';
      case ApprovedStatus.Rejected:
        return 'courses.rejected';
      case ApprovedStatus.UnAccepted:
        return 'courses.join course';
      case ApprovedStatus.UnApproved:
        return 'Pending';
      default:
        return '-';
    }
  }

  get isOwner(): boolean {
    return this.course.author === GetUserId();
  }

  get isUserAdmin(): boolean {
    return this._permissionService.isAdmin();
  }

  openCourseDetailDialog() {
    this._dialogService.open(CourseDetailsComponent, {
      width: '70vw',
      closeButton: false,
      windowClass: 'dialog-overflow-y-auto',
      data: {
        courseId: this.course.id,
        currentCourseDate: this.course.currentDate,
      },
    });
  }

  join(data?: CourseEntity) {
    this.joinCourse.emit(data || this.course);
  }

  edit(data: CourseEntity) {
    this.editCourse.emit(data || this.course);
  }

  delete(data: CourseEntity) {
    this.deleteCourse.emit(data || this.course);
  }
}
